import { type AddToCartHookProps } from "~/features/addToCart/hooks/useAddToCartForm";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import type { CloseIntent } from "~/features/addToCart/flow/useAtcFlowController";
import {
  AtcWizardProviders,
  type AddToCartSubmitHandler,
  useAtcWizardProcess,
} from "~/features/addToCart/components/AddToCartProcess/AddToCartWizardShared";
import { AtcWizardScreens } from "~/features/addToCart/components/AddToCartProcess/AtcWizardScreens";
import { resolveEditPartScreen } from "~/features/addToCart/flow/resolveEditPartScreen";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";

export interface AddToCartWizardProps extends Omit<AddToCartHookProps, "priceReferenceSelection"> {
  restrictionQuery: string;
  onRequestClose: (intent: CloseIntent) => void;
  onSubmit: AddToCartSubmitHandler;
}

export const EditPartWizard = ({ restrictionQuery, onRequestClose, onSubmit, ...hookProps }: AddToCartWizardProps) => {
  const { selection, status } = usePriceReferenceSelection();

  if (status !== "ready") {
    return null;
  }

  return (
    <ReadyEditPartWizard
      {...hookProps}
      restrictionQuery={restrictionQuery}
      onRequestClose={onRequestClose}
      onSubmit={onSubmit}
      priceReferenceSelection={selection}
    />
  );
};

const ReadyEditPartWizard = ({
  restrictionQuery,
  onRequestClose,
  onSubmit,
  priceReferenceSelection,
  ...hookProps
}: AddToCartWizardProps & { priceReferenceSelection: PriceReferenceSelection | undefined }) => {
  const process = useAtcWizardProcess({
    flowKind: "editPart",
    hookProps: { ...hookProps, priceReferenceSelection },
    onRequestClose,
    onSubmit,
    resolveScreen: resolveEditPartScreen,
    restrictionQuery,
  });

  return (
    <AtcWizardProviders derivedCtx={process.derivedCtx} flowContextProps={process.flowContextProps} form={process.form}>
      <AtcWizardScreens
        canSeeAddToCartDisclaimer={process.canSeeAddToCartDisclaimer}
        canSubmitToCartOrQuote={process.canSubmitToCartOrQuote}
        flowKind="editPart"
        isSending={process.isSending}
        productDescriptor={process.productDescriptor}
        screen={process.currentScreen}
        submit={process.submit}
      />
    </AtcWizardProviders>
  );
};
